import React, { useState } from 'react';

/**
 * GalleryItem - Reusable component for displaying a single gallery item
 */
const GalleryItem = ({ 
  item, 
  onView, 
  onDownload, 
  onDelete,
  onToggleFavorite,
  showActions = true,
  size = 'medium', // 'small', 'medium', 'large'
  selectMode = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  const isFavorite = !!item.is_favorite;

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    if (onToggleFavorite) onToggleFavorite(item, !isFavorite);
  };

  const handleSelectClick = (e) => {
    e.stopPropagation();
    if (onToggleSelect) onToggleSelect(item);
  };

  const sizeClasses = {
    small: 'h-32',
    medium: 'h-48',
    large: 'h-64'
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (onDownload) {
      onDownload(item);
    } else {
      // Default download behavior
      const link = document.createElement('a');
      link.href = item.url;
      link.download = item.filename || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(item);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const formatSize = (mb) => {
    if (mb < 1) return `${Math.round(mb * 1024)} KB`;
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <div
      className={`relative rounded-xl overflow-hidden cursor-pointer group bg-gray-100 dark:bg-gray-800 transition-all ${
        selectMode && isSelected ? 'ring-4 ring-yellow-400 ring-offset-2 dark:ring-offset-slate-900' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        if (selectMode) { onToggleSelect && onToggleSelect(item); return; }
        onView && onView(item);
      }}
      data-testid={`gallery-item-${item.id}`}
    >
      {/* Selection checkbox — visible in selectMode */}
      {selectMode && (
        <div
          onClick={handleSelectClick}
          className={`absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center shadow-lg border-2 ${
            isSelected
              ? 'bg-yellow-400 border-yellow-400 text-slate-900'
              : 'bg-white/90 border-white text-transparent'
          }`}
          data-testid={`gallery-item-select-${item.id}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
        </div>
      )}
      {/* Media Content */}
      <div className={`${sizeClasses[size]} w-full`}>
        {item.type === 'video' ? (
          <>
            <video
              src={item.url}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              poster={item.thumbnail}
              onMouseEnter={(e) => e.target.play().catch(() => {})}
              onMouseLeave={(e) => e.target.pause()}
            />
            {/* Video Badge */}
            <div className="absolute top-2 right-2">
              <span className="px-2 py-1 bg-red-500/90 text-white text-xs font-bold rounded-full flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                VIDEO
              </span>
            </div>
          </>
        ) : (
          <img
            src={imageError ? '/placeholder-image.png' : item.url}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={() => setImageError(true)}
          />
        )}
      </div>

      {/* Overlay with Info */}
      <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-70'}`}>
        {/* Persistent favorite badge (top-left, visible even when not hovered) */}
        {isFavorite && (
          <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-yellow-400 text-white flex items-center justify-center shadow-lg text-sm" title="Favorited">
            ★
          </div>
        )}
        {/* Bottom Info */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <p className="text-white font-medium text-sm truncate">{item.title}</p>
          <div className="flex items-center justify-between mt-1">
            <span className="text-gray-300 text-xs">{formatDate(item.created_at)}</span>
            <span className="text-gray-300 text-xs">{formatSize(item.size_mb)}</span>
          </div>
        </div>

        {/* Action Buttons - Show on Hover (disabled in select mode to avoid overlapping the checkbox) */}
        {showActions && isHovered && !selectMode && (
          <div className="absolute top-2 right-2 flex gap-2">
            {onToggleFavorite && (
              <button
                onClick={handleToggleFavorite}
                className={`p-2 rounded-full shadow-lg transition-all ${
                  isFavorite ? 'bg-yellow-400 hover:bg-yellow-500 text-white' : 'bg-white/90 hover:bg-yellow-100 text-slate-700'
                }`}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                data-testid={isFavorite ? 'gallery-unfavorite-btn' : 'gallery-favorite-btn'}
              >
                <span className="block w-4 h-4 text-base leading-4">{isFavorite ? '★' : '☆'}</span>
              </button>
            )}
            <button
              onClick={handleDownload}
              className="p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all"
              title="Download"
              data-testid="gallery-download-btn"
            >
              <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
            {onDelete && (
              <button
                onClick={handleDelete}
                className="p-2 bg-red-500/90 hover:bg-red-500 rounded-full shadow-lg transition-all"
                title="Delete"
                data-testid="gallery-delete-btn"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryItem;
